import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { map, cond, always, T, prop } from 'ramda'
import { insertIf } from 'utils/types'
import strings from 'utils/strings'
import { CREATE_FEATURE_COLLECTION } from 'constants/authorization'
import { useSelector } from 'hooks/redux'
import { hasRight } from 'selectors/authorization'
import { Button, Paragraph } from 'components/patterns'
import { FEATURES } from 'utils/constants/vehicle-constants'

const ActionButtons = ({
  showCommitView,
  disableConfirmSelection,
  disableAddFeature,
  showConflictingFeaturesBtns,
  showConflictingSwitchesBtns,
  dirty,
  commitMessage,
  handleConfirmSelection,
  handleContinueAndAddSwitch,
  handleCommitUpdates,
  handleAddFeature,
  isUpdating,
  showConflictOverviewBtns,
  allConflictsResolved,
  hasUnmappedSwitches,
  handleReopenSwitchMapping,
  handleReviewFeatures,
  isOverResourceLimits,
  handleToggleFeatureCollectionModal,
  isValidating,
  showCreateFeatureCollBtn,
  isValidatingDynamicFeatures,
}) => {
  const canCreateFeatureCollection = useSelector(
    hasRight,
    CREATE_FEATURE_COLLECTION
  )

  const commitButtonDisabled = useMemo(
    () =>
      !dirty ||
      // Bug #505985: outstanding switch mappings must block commit on the normal
      // footer too, not only inside the conflict overview. Since switch mapping now
      // opens directly (without routing through CONFLICTS_OVER_VIEW and its
      // allConflictsResolved gate), dismissing the mapping modal would otherwise
      // leave the feature staged with unassigned signals and commit enabled.
      // `null` means "no mapping step applies"; only an explicit false blocks.
      hasUnmappedSwitches ||
      isOverResourceLimits ||
      (showCommitView && !commitMessage) ||
      isUpdating ||
      isValidatingDynamicFeatures,
    [
      dirty,
      hasUnmappedSwitches,
      showCommitView,
      commitMessage,
      isUpdating,
      isOverResourceLimits,
      isValidatingDynamicFeatures,
    ]
  )

  const isFeatureConflict = prop('showConflictingFeaturesBtns')
  const isSwitchConflict = prop('showConflictingSwitchesBtns')
  const isOverview = prop('showConflictOverviewBtns')

  const SWITCHES = 'SWITCHES'
  const NO_CONFLICT = 'NO_CONFLICT'
  const CONFLICTS_OVER_VIEW = 'CONFLICTS_OVER_VIEW'

  const getConflictType = cond([
    [isOverview, always(CONFLICTS_OVER_VIEW)],
    [isFeatureConflict, always(FEATURES)],
    [isSwitchConflict, always(SWITCHES)],
    [T, always(NO_CONFLICT)],
  ])

  const conditions = {
    showConflictingFeaturesBtns,
    showConflictingSwitchesBtns,
    showConflictOverviewBtns,
  }

  const conflictType = getConflictType(conditions)

  const conflictButtons = {
    [CONFLICTS_OVER_VIEW]: [
      {
        onClick: handleReviewFeatures,
        disabled: !allConflictsResolved,
        text: strings.review_features,
      },
      {
        blurb: !allConflictsResolved ? (
          <UnresolvedConflictsBlurb key="UnresolvedConflictsBlurb">
            {strings.features_cannot_be_added}
          </UnresolvedConflictsBlurb>
        ) : (
          <></>
        ),
      },
    ],
    [FEATURES]: [
      {
        onClick: handleConfirmSelection,
        type: 'primary',
        disabled: disableConfirmSelection,
        text: strings.confirm_selection,
      },
    ],
    [SWITCHES]: [
      {
        onClick: handleContinueAndAddSwitch,
        type: 'primary',
        disabled: false,
        text: strings.continue_and_add_switch,
      },
    ],
  }

  const addFeatureButtonType = commitButtonDisabled ? 'primary' : 'secondary'
  const addFeatureButtonFill = disableAddFeature
    ? 'rgba(140,150,162,1)'
    : addFeatureButtonType === 'primary'
    ? '#fff'
    : 'rgba(11, 46, 107, 1)'
  const showCreateFeatureCollection =
    canCreateFeatureCollection && !showCommitView && showCreateFeatureCollBtn

  const editCommitButtons = [
    // Bug #505985: the only way back into an outstanding switch mapping after the
    // modal is dismissed. The staged-features table has no switch cell to click —
    // AddRemoveFeaturesTable's custom column is the remove checkbox, and its
    // handleChangeSwitchSelection prop has been commented out at the destructure
    // since before this work — so without this the disabled Commit below is a dead
    // end. Mirrors the conflict overview's blurb-next-to-disabled-button pattern.
    ...insertIf(hasUnmappedSwitches && !showCommitView, {
      onClick: () => handleReopenSwitchMapping(),
      type: 'secondary',
      text: strings.select_switchs,
      iconProps: { icon: 'link', fill: 'rgba(11, 46, 107, 1)', width: 12 },
    }),
    ...insertIf(showCreateFeatureCollection, {
      onClick: () => handleToggleFeatureCollectionModal(),
      text: strings.save_new_collection,
      iconProps: { icon: 'new_window', fill: 'bluePrimary', width: 12 },
      type: 'secondary',
    }),
    ...insertIf(!showCommitView, {
      onClick: () => handleAddFeature(),
      type: addFeatureButtonType,
      disabled: disableAddFeature && conflictType === NO_CONFLICT,
      loading: isValidating,
      text: isValidating
        ? strings.checking_for_conflicts
        : strings.add_features,
      ...(!isValidating && {
        iconProps: { icon: 'add', fill: addFeatureButtonFill, width: 12 },
      }),
    }),
    {
      onClick: handleCommitUpdates,
      type: 'primary',
      disabled: commitButtonDisabled,
      text: isValidatingDynamicFeatures
        ? strings.validating_features
        : strings.commit_to_vehicle,
      loading: isUpdating || isValidatingDynamicFeatures,
    },
  ]

  const buttonsToRender =
    conflictType !== NO_CONFLICT
      ? conflictButtons[conflictType]
      : editCommitButtons

  const renderButtons = map((buttonProps) => {
    const { text, blurb, ...rest } = buttonProps

    if (blurb) return blurb

    return (
      <Button key={text} {...rest} style={{ marginRight: 8, marginLeft: 8 }}>
        {text}
      </Button>
    )
  })

  return renderButtons(buttonsToRender)
}

ActionButtons.propTypes = {
  showCommitView: PropTypes.bool,
  disableAddFeature: PropTypes.bool,
  disableConfirmSelection: PropTypes.bool,
  showConflictingFeaturesBtns: PropTypes.bool,
  dirty: PropTypes.bool,
  /** true when a staged feature still has signals awaiting switch assignment */
  hasUnmappedSwitches: PropTypes.bool,
  /** reopens the switch mapping modal — the route out of a blocked commit */
  handleReopenSwitchMapping: PropTypes.func,
  commitMessage: PropTypes.string,
  handleAcceptCurrentConflicts: PropTypes.func,
  handleConfirmSelection: PropTypes.func,
  handleAddFeature: PropTypes.func,
  showConflictingSwitchesBtns: PropTypes.bool,
  handleContinueAndAddSwitch: PropTypes.func,
  isUpdating: PropTypes.bool,
  showConflictOverviewBtns: PropTypes.bool,
  allConflictsResolved: PropTypes.bool,
  isOverResourceLimits: PropTypes.bool,
  handleToggleFeatureCollectionModal: PropTypes.func,
  isValidating: PropTypes.bool,
  showCreateFeatureCollBtn: PropTypes.bool,
  isValidatingDynamicFeatures: PropTypes.bool,
}

export default ActionButtons

const UnresolvedConflictsBlurb = styled(Paragraph)`
  display: inline-block;
  font-size: 12px;
  width: 232px;
  position: relative;
  top: 6px;
  left: 5px;
  text-align: left;
`
